import { CustomQuestionTestingData, GAME_STAGES } from '.';
import { findIndexOf2dArray, roundToOneDecimal } from '../room/utils';

// All intervals in seconds
const COUNTDOWN_INTERVAL = 1;
const UNPAUSE_COUNT_INTERVAL = 1;
const DELAY_BEFORE_QUESTION_REVEAL_INTERVAL = 1;
const GAMECLOCK_INTERVAL = 1;
const REVEAL_QUESTION_INTERVAL = 0.5;
const DELAY_BEFORE_LETTER_REVEAL_INTERVAL = 1;
const REVEAL_BOXES_INTERVAL = 0.5;
const NEXT_QUESTION_INTERVAL = 1;

const isInterval = (x, y) => Math.round(x * 100) % Math.round(y * 100) === 0;

const handleCountdown = (seconds: number, data: CustomQuestionTestingData) => {
  if (!isInterval(seconds, COUNTDOWN_INTERVAL)) return;
  const isGameStarted = data.gameStarted;
  if (!isGameStarted) return;
  data.countdown = data.countdown - 1;
  if (data.countdown === 0) {
    data.gameStage = GAME_STAGES.DELAY_BEFORE_QUESTION_REVEAL;
    return;
  }
};

const handleDelayBeforeQuestionReveal = async (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  if (!isInterval(seconds, DELAY_BEFORE_QUESTION_REVEAL_INTERVAL)) return;
  if (data.delayBeforeQuestionRevealCount <= 0) {
    data.gameStage = GAME_STAGES.REVEAL_QUESTION;
    return;
  }
  data.delayBeforeQuestionRevealCount = data.delayBeforeQuestionRevealCount - 1;
};

const handleGameClock = async (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  if (!isInterval(seconds, GAMECLOCK_INTERVAL)) return;
  data.gameClock = data.gameClock + 1;
  if (data.gameClock >= 20) {
    data.gameStage = GAME_STAGES.NEXT_QUESTION;
  }
};

const handleRevealQuestion = async (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  if (!isInterval(seconds, REVEAL_QUESTION_INTERVAL)) return;
  if (data.questionWordIndex >= data.questionWords.length) {
    data.revealAnswer = data.revealedLetters;
    data.gameStage = GAME_STAGES.REVEAL_BOXES;
  }
  data.revealQuestion = data.questionWords
    .slice(0, data.questionWordIndex + 1)
    .join(' ');
  data.questionWordIndex = data.questionWordIndex + 1;
};

const handleRevealBoxes = async (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  if (!isInterval(seconds, REVEAL_BOXES_INTERVAL)) return;
  // console.log('REVEAL_BOXES', seconds, revealBoxesCount);
  if (data.revealBoxesCount <= 0) {
    data.gameStage = GAME_STAGES.DELAY_BEFORE_LETTER_REVEAL;
  }
  data.revealBoxesCount = data.revealBoxesCount - 1;
};

const handleDelayBeforeLetterReveal = async (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  if (!isInterval(seconds, DELAY_BEFORE_LETTER_REVEAL_INTERVAL)) return;
  // console.log('DELAY_BEFORE_LETTER_REVEAL', seconds, count);
  if (data.delayBeforeLetterRevealCount <= 0) {
    data.gameStage = GAME_STAGES.REVEAL_ANSWER;
    const revealModOffset = roundToOneDecimal(seconds % data.revealTiming);
    data.revealModOffset = revealModOffset;
  }
  data.delayBeforeLetterRevealCount = data.delayBeforeLetterRevealCount - 1;
};

const handleRevealAnswer = async (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  // guarantees that the first letter is revealed instantly
  const adjustedSeconds = seconds - data.revealModOffset - 0.1;
  if (!isInterval(adjustedSeconds, data.revealTiming)) return;
  const revealedLetters = data.revealedLetters;
  const unrevealedProperNameIndex = data.unrevealedProperNameIndex;
  const [randomWordIndex, randomLetterIndex] = getRandomIndexInArray(
    revealedLetters,
    unrevealedProperNameIndex
  );
  if (randomWordIndex === -1 && randomLetterIndex === -1) {
    data.unrevealedIndex = findIndexOf2dArray(revealedLetters, '');
    data.gameStage = GAME_STAGES.WAIT_UNTIL_TIME_LIMIT;
    return;
  }
  data.revealedLetters[randomWordIndex][randomLetterIndex] =
    data.answerLetters[randomWordIndex][randomLetterIndex];
  console.log('data.revealAnswer', JSON.stringify(data.revealAnswer));
  data.revealAnswer = revealedLetters;
};

// const handleNextQuestion = async (seconds: number, io: any, gameId: string) => {
//   if (!isInterval(seconds, NEXT_QUESTION_INTERVAL)) return;
//   const timerLimitReached = getTimerLimitReached(gameId);
//   if (!timerLimitReached) {
//     const answerLetters = getAnswerLetters(gameId);
//     setRevealAnswer(io, gameId, answerLetters);
//   }
//   const nextQuestionDelayCount = getNextQuestionDelayCount(gameId);
//   io.to(gameId).emit('nextQuestionDelayCount', nextQuestionDelayCount);
//   if (nextQuestionDelayCount <= 0) {
//     await resetStatesForNextQuestion(io, gameId);
//     await updateGameOnAllServers(gameId);
//     return;
//   }
//   setNextQuestionDelayCount(gameId, nextQuestionDelayCount - 1);
// };

export const updateGame = (
  seconds: number,
  data: CustomQuestionTestingData
) => {
  const gameStage = data.gameStage;
  if (gameStage === GAME_STAGES.COUNT_DOWN) {
    handleCountdown(seconds, data);
  }
  if (gameStage === GAME_STAGES.DELAY_BEFORE_QUESTION_REVEAL) {
    handleDelayBeforeQuestionReveal(seconds, data);
  }
  if (
    gameStage !== GAME_STAGES.COUNT_DOWN &&
    gameStage !== GAME_STAGES.DELAY_BEFORE_QUESTION_REVEAL &&
    gameStage !== GAME_STAGES.NEXT_QUESTION
  ) {
    handleGameClock(seconds, data);
  }
  if (
    gameStage === GAME_STAGES.REVEAL_QUESTION &&
    gameStage !== GAME_STAGES.WAIT_UNTIL_TIME_LIMIT
  ) {
    handleRevealQuestion(seconds, data);
  }
  if (
    gameStage === GAME_STAGES.REVEAL_BOXES &&
    gameStage !== GAME_STAGES.WAIT_UNTIL_TIME_LIMIT
  ) {
    handleRevealBoxes(seconds, data);
  }
  if (
    gameStage === GAME_STAGES.DELAY_BEFORE_LETTER_REVEAL &&
    gameStage !== GAME_STAGES.WAIT_UNTIL_TIME_LIMIT
  ) {
    handleDelayBeforeLetterReveal(seconds, data);
  }
  if (
    gameStage === GAME_STAGES.REVEAL_ANSWER &&
    gameStage !== GAME_STAGES.WAIT_UNTIL_TIME_LIMIT
  ) {
    handleRevealAnswer(seconds, data);
  }
  return data;
};

const getRandomIndexInArray = (array, properNameIndex = undefined) => {
  // leave 1 letter unrevealed
  // if all letters have been revealed except 1, return [-1, -1]
  if (array.flat().filter((e) => e === '').length === 1) return [-1, -1];
  while (true) {
    const randomWordIndex = Math.floor(Math.random() * array.length);
    const randomLetterIndex = Math.floor(
      Math.random() * array[randomWordIndex].length
    );
    if (
      properNameIndex &&
      randomWordIndex === properNameIndex[0] &&
      randomLetterIndex === properNameIndex[1]
    )
      continue;
    if (array[randomWordIndex][randomLetterIndex] === '')
      return [randomWordIndex, randomLetterIndex];
  }
};
